(() => {
  const qs = (s, scope = document) => scope.querySelector(s);
  const qsa = (s, scope = document) => [...scope.querySelectorAll(s)];

  function initMobileMenu(scope = document) {
    const header = qs('[data-header]', scope) || qs('[data-header]');
    if (!header || header.dataset.menuReady === 'true') return;
    header.dataset.menuReady = 'true';
    const menu = qs('[data-mobile-menu]', header);
    const overlay = qs('[data-menu-overlay]', header);
    const open = qs('[data-menu-toggle]', header);
    const close = qs('[data-menu-close]', header);
    const setOpen = (value) => {
      if (!menu || !overlay || !open) return;
      menu.hidden = !value;
      overlay.hidden = !value;
      open.setAttribute('aria-expanded', String(value));
      document.documentElement.classList.toggle('menu-open', value);
      if (value) close?.focus();
    };
    open?.addEventListener('click', () => setOpen(true));
    close?.addEventListener('click', () => setOpen(false));
    overlay?.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
  }

  function initBeforeAfter(scope = document) {
    qsa('[data-before-after]', scope).forEach((el) => {
      if (el.dataset.ready === 'true') return;
      el.dataset.ready = 'true';
      const range = qs('[data-before-after-range]', el);
      if (!range) return;

      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      const update = (value = range.value) => {
        const position = clamp(Number(value) || 50, 0, 100);
        range.value = String(position);
        el.style.setProperty('--position', `${position}%`);
        el.setAttribute('aria-valuenow', String(Math.round(position)));
      };

      const updateFromPointer = (clientX) => {
        const rect = el.getBoundingClientRect();
        if (!rect.width) return;
        const position = ((clientX - rect.left) / rect.width) * 100;
        update(position);
      };

      let dragging = false;

      el.addEventListener('pointerdown', (event) => {
        if (event.button !== undefined && event.button !== 0) return;
        dragging = true;
        el.classList.add('is-dragging');
        try { el.setPointerCapture(event.pointerId); } catch (e) {}
        updateFromPointer(event.clientX);
        event.preventDefault();
      });

      el.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        updateFromPointer(event.clientX);
        event.preventDefault();
      });

      const stopDragging = (event) => {
        if (!dragging) return;
        dragging = false;
        el.classList.remove('is-dragging');
        try { el.releasePointerCapture(event.pointerId); } catch (e) {}
      };

      el.addEventListener('pointerup', stopDragging);
      el.addEventListener('pointercancel', stopDragging);

      range.addEventListener('input', () => update(range.value));
      range.addEventListener('change', () => update(range.value));

      update(range.value);
    });
  }

  function initProductForms(scope = document) {
    qsa('[data-product-form]', scope).forEach((form) => {
      if (form.dataset.ready === 'true') return;
      form.dataset.ready = 'true';
      const section = form.closest('.main-product');
      const radios = qsa('[data-option-position]', section);
      const idInput = qs('[data-variant-id]', form);
      if (!radios.length || !idInput) return;
      const productData = qs('script[type="application/json"][data-product-json]', section);
      if (!productData) return;
      let variants = [];
      try { variants = JSON.parse(productData.textContent); } catch (e) { return; }

      // Price and inventory templates are rendered by Liquid for every variant.
      // This preserves the merchant's Shopify money format and market currency.
      const priceWrapper = qs('[data-product-variant-price]', section);
      const inventoryWrapper = qs('[data-product-inventory]', section);
      const updateVariantDisplay = (variant) => {
        const variantId = String(variant.id);
        const priceTemplate = qs(`template[data-variant-price="${variantId}"]`, section);
        const currentPrice = priceWrapper && qs('.price', priceWrapper);
        const nextPrice = priceTemplate && qs('.price', priceTemplate.content);
        if (currentPrice && nextPrice) {
          const currentAmount = qs('.price__current', currentPrice);
          const nextAmount = qs('.price__current', nextPrice);
          if (currentAmount && nextAmount) currentAmount.innerHTML = nextAmount.innerHTML;

          const oldCompare = qs('.price__compare', currentPrice);
          const newCompare = qs('.price__compare', nextPrice);
          if (newCompare) {
            if (oldCompare) oldCompare.innerHTML = newCompare.innerHTML;
            else currentPrice.appendChild(newCompare.cloneNode(true));
          } else if (oldCompare) {
            oldCompare.remove();
          }
          currentPrice.classList.toggle('price--sale', nextPrice.classList.contains('price--sale'));
          currentPrice.classList.toggle('price--sold-out', nextPrice.classList.contains('price--sold-out'));
        }
        if (inventoryWrapper) {
          const stockTemplate = qs(`template[data-variant-inventory="${variantId}"]`, section);
          if (stockTemplate) inventoryWrapper.replaceChildren(stockTemplate.content.cloneNode(true));
          inventoryWrapper.classList.toggle('is-available', Boolean(variant.available));
        }
        if (priceWrapper) priceWrapper.hidden = false;
        if (inventoryWrapper) inventoryWrapper.hidden = false;
      };

      const updateVariant = () => {
        const selected = [];
        qsa('[data-option-position]', section).forEach((radio) => {
          if (radio.checked) selected[Number(radio.dataset.optionPosition) - 1] = radio.value;
        });
        const variant = variants.find((v) => v.options.every((opt, index) => opt === selected[index]));
        const button = qs('button[name="add"]', form);
        const label = qs('[data-add-to-cart-text]', form);
        if (!variant) {
          if (idInput.value) {
            idInput.value = '';
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (button) button.disabled = true;
          if (label) label.textContent = form.dataset.unavailableLabel || window.theme?.strings?.unavailable || 'Unavailable';
          if (priceWrapper) priceWrapper.hidden = true;
          if (inventoryWrapper) inventoryWrapper.hidden = true;
          return;
        }
        if (idInput.value !== String(variant.id)) {
          idInput.value = variant.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        updateVariantDisplay(variant);
        if (button) button.disabled = !variant.available;
        if (label) label.textContent = variant.available ? (form.dataset.addLabel || window.theme?.strings?.addToCart || 'Add to cart') : (form.dataset.soldLabel || window.theme?.strings?.soldOut || 'Sold out');
        qsa('[data-option-position]', section).forEach((radio) => {
          if (!radio.checked) return;
          const selectedLabel = qs(`[data-option-selected="${radio.dataset.optionPosition}"]`, section);
          if (selectedLabel) selectedLabel.textContent = radio.value;
        });
        const featuredMediaId = variant.featured_media?.id || variant.featured_media_id;
        if (featuredMediaId) {
          const gallery = qs('[data-product-gallery]', section);
          gallery?.dispatchEvent(new CustomEvent('glory:show-media', { detail: { mediaId: featuredMediaId } }));
        }
        if (history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
      };
      radios.forEach((radio) => radio.addEventListener('change', updateVariant));
      updateVariant();
    });
  }


  function initProductGallery(scope = document) {
    qsa('[data-product-gallery]', scope).forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';
      const thumbs = qsa('[data-product-thumb]', gallery);
      const items = qsa('[data-product-media]', gallery);
      const prev = qs('[data-gallery-prev]', gallery);
      const next = qs('[data-gallery-next]', gallery);
      const counter = qs('[data-gallery-current]', gallery);
      if (!items.length) return;

      let index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));

      const show = (nextIndex) => {
        index = (nextIndex + items.length) % items.length;
        items.forEach((item, i) => {
          const active = i === index;
          item.hidden = !active;
          item.classList.toggle('is-active', active);
          const media = item.querySelector('video, model-viewer');
          if (!active && media && typeof media.pause === 'function') media.pause();
        });
        thumbs.forEach((thumb, i) => {
          const active = i === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
        });
        if (counter) counter.textContent = String(index + 1);
      };

      thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => show(thumbIndex)));
      prev?.addEventListener('click', () => show(index - 1));
      next?.addEventListener('click', () => show(index + 1));

      gallery.addEventListener('glory:show-media', (event) => {
        const id = String(event.detail?.mediaId || '');
        const targetIndex = items.findIndex((item) => String(item.dataset.mediaId) === id);
        if (targetIndex >= 0) show(targetIndex);
      });
      show(index);
    });
  }

  function initQuantityControls(scope = document) {
    qsa('[data-quantity-control]', scope).forEach((control) => {
      if (control.dataset.quantityReady === 'true') return;
      control.dataset.quantityReady = 'true';
      const input = qs('[data-quantity-input]', control);
      const minus = qs('[data-quantity-minus]', control);
      const plus = qs('[data-quantity-plus]', control);
      if (!input) return;
      const normalize = (value) => Math.max(Number(input.min || 1), Number(value) || 1);
      minus?.addEventListener('click', () => { input.value = String(normalize(Number(input.value) - 1)); input.dispatchEvent(new Event('change', { bubbles: true })); });
      plus?.addEventListener('click', () => { input.value = String(normalize(Number(input.value) + 1)); input.dispatchEvent(new Event('change', { bubbles: true })); });
    });
  }

  function initProductRecommendations(scope = document) {
    qsa('[data-product-recommendations]', scope).forEach((container) => {
      if (container.dataset.recommendationsReady === 'true') return;
      container.dataset.recommendationsReady = 'true';
      const url = container.dataset.url;
      if (!url || container.querySelector('.glory-product-recommendations')) return;
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.querySelector('[data-product-recommendations]');
          if (fresh && fresh.innerHTML.trim()) container.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
  }

  function init() {
    initMobileMenu();
    initBeforeAfter();
    initProductGallery();
    initProductForms();
    initQuantityControls();
    initProductRecommendations();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', (event) => {
    initBeforeAfter(event.target);
    initMobileMenu(event.target);
    initProductGallery(event.target);
    initProductForms(event.target);
    initQuantityControls(event.target);
    initProductRecommendations(event.target);
  });
})();
